import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center mb-8">
    {[1, 2, 3].map((step) => {
      let stepClasses = 'bg-white/10 text-[#d8d2c5]';
      if (step === currentStep) {
        stepClasses = 'bg-emerald-500 text-white scale-110';
      } else if (step < currentStep) {
        stepClasses = 'bg-emerald-500/50 text-white';
      }

      return (
        <React.Fragment key={step}>
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all ${stepClasses}`}
          >
            {step < currentStep ? '✓' : step}
          </div>
          {step < 3 && (
            <div
              className={`w-16 h-1 mx-2 rounded transition-all ${step < currentStep ? 'bg-emerald-500' : 'bg-white/10'}`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

/**
 * Progressive Registration Component
 * Multi-step registration: Step 1 (Email) → Step 2 (Password) → Step 3 (Name & Phone)
 * Completes in 3 easy steps with auto-focus and validation
 */
const ProgressiveRegister = () => {
  const [searchParams] = useSearchParams();
  const signupToken = searchParams.get('token');
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { addToCart } = useCart();

  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirmation: '',
    name: '',
    phone_number: '',
    signup_token: signupToken || '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
  const [tokenData, setTokenData] = useState(null); // Store verified token data

  // Verify signup token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!signupToken) return;

      try {
        const response = await api.get(`/public/signup-token/${signupToken}`);
        const { data } = response;
        if (!data || data.valid !== true) {
          showToast('Invalid or expired payment token', 'error');
          return;
        }

        setTokenData(data);

        // Prefill form with payment data
        setFormData((prev) => ({
          ...prev,
          email: data.email || prev.email,
          phone_number: data.phone_number || prev.phone_number,
          signup_token: signupToken,
        }));

        // Add plan to cart if available
        if (data.plan?.id) {
          addToCart(data.plan);
        }

        showToast('Payment verified! Please complete registration.', 'success');
      } catch (error) {
        console.error('Token verification error:', error);
        showToast(error?.message || 'Failed to verify payment token', 'error');
      }
    };

    verifyToken();
  }, [signupToken, addToCart, showToast]);

  useEffect(() => {
    if (signupToken) {
      setFormData((prev) => ({ ...prev, signup_token: signupToken }));
    }
  }, [signupToken]);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;

    setFormData((prev) => ({
      ...prev,
      email: prefill.email || prev.email,
      phone_number: prefill.phone_number || prev.phone_number,
    }));
  }, [location.state]);

  useEffect(() => {
    const statePlan = location.state?.selectedPlan || location.state?.plan;

    let sessionPlan = null;
    const rawSessionPlan = sessionStorage.getItem('selectedPlan');
    if (rawSessionPlan) {
      try {
        sessionPlan = JSON.parse(rawSessionPlan);
      } catch (e) {
        sessionStorage.removeItem('selectedPlan');
      }
    }

    const planToRestore = statePlan || sessionPlan;
    if (planToRestore?.id) {
      addToCart(planToRestore);
    }
  }, [addToCart, location.state]);
  const handleSocialAuth = async (provider) => {
    try {
      const params = new URLSearchParams();
      if (signupToken) {
        params.append('token', signupToken);
      }

      window.location.href = `/api/v1/auth/social/${provider}?${params.toString()}`;
    } catch (error) {
      showToast(`Failed to sign up with ${provider}`, 'error');
      console.error(`${provider} auth error:`, error);
    }
  };

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };

    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    let label = '';
    let color = '';
    if (score <= 2) {
      label = 'Weak';
      color = 'bg-red-500';
    } else if (score <= 4) {
      label = 'Fair';
      color = 'bg-yellow-500';
    } else if (score <= 5) {
      label = 'Good';
      color = 'bg-emerald-500';
    } else {
      label = 'Strong';
      color = 'bg-emerald-600';
    }

    return { score, label, color };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Validate current step
  const validateStep = () => {
    const stepErrors = {};

    if (currentStep === 1) {
      if (!formData.email) {
        stepErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        stepErrors.email = 'Email is invalid';
      }
    } else if (currentStep === 2) {
      if (!formData.password) {
        stepErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        stepErrors.password = 'Password must be at least 8 characters';
      }
      if (formData.password !== formData.password_confirmation) {
        stepErrors.password_confirmation = 'Passwords do not match';
      }
    } else if (currentStep === 3) {
      if (!formData.name) {
        stepErrors.name = 'Name is required';
      }
    }

    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    const result = await register(formData);
    setLoading(false);

    if (result.success) {
      if (signupToken) {
        navigate('/onboarding', { replace: true, state: { newSubscription: true } });
        return;
      }

      const returnTo =
        location.state?.returnTo || sessionStorage.getItem('returnToAfterAuth') || '/dashboard';

      sessionStorage.removeItem('returnToAfterAuth');
      navigate(returnTo, { replace: true });
    } else if (result.errors) {
      setErrors(result.errors);
    } else {
      setErrors({ general: result.message });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f1115] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full animate-fadeIn">
        {tokenData && (
          <div className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-emerald-400 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <div className="flex-1">
                <div className="text-sm text-emerald-200 font-semibold">Payment Confirmed</div>
                <div className="text-xs text-[#d8d2c5] mt-1">
                  {tokenData.plan?.name} - KES {tokenData.amount?.toLocaleString()}
                </div>
                {tokenData.receipt_number && (
                  <div className="text-xs text-[#d8d2c5] mt-1">
                    Receipt: {tokenData.receipt_number}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            {/* Logo Icon */}
            <div className="relative flex-shrink-0">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-cyan-500 to-amber-500 rounded-2xl blur-xl opacity-20 animate-pulse" />

              {/* Main Logo */}
              <div className="relative bg-gradient-to-br from-[#1a1f2e] to-[#0f1115] rounded-2xl p-3 border border-white/10 backdrop-blur-sm">
                <svg
                  className="w-14 h-14"
                  viewBox="0 0 120 120"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Circular Background Accent */}
                  <circle cx="60" cy="60" r="55" fill="url(#logoGradient)" opacity="0.1" />

                  {/* Interlocking Circles - Representing Unity & Collaboration */}
                  <circle
                    cx="45"
                    cy="55"
                    r="18"
                    stroke="url(#logoGradient)"
                    strokeWidth="3.5"
                    fill="none"
                    opacity="0.8"
                  />
                  <circle
                    cx="75"
                    cy="55"
                    r="18"
                    stroke="url(#logoGradient)"
                    strokeWidth="3.5"
                    fill="none"
                    opacity="0.8"
                  />

                  {/* Center Diamond - Representing Value & Trust */}
                  <path d="M60 40 L70 55 L60 70 L50 55 Z" fill="url(#logoGradient)" opacity="0.9" />

                  {/* Coins Stack - Financial Symbol */}
                  <ellipse cx="60" cy="75" rx="15" ry="4" fill="url(#logoGradient)" opacity="0.6" />
                  <ellipse cx="60" cy="72" rx="15" ry="4" fill="url(#logoGradient)" opacity="0.7" />
                  <ellipse cx="60" cy="69" rx="15" ry="4" fill="url(#logoGradient)" opacity="0.8" />

                  {/* Connecting Lines - Community Network */}
                  <path
                    d="M45 73 Q60 80 75 73"
                    stroke="url(#logoGradient)"
                    strokeWidth="2"
                    opacity="0.5"
                    strokeLinecap="round"
                  />

                  {/* Gradient Definition */}
                  <defs>
                    <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="50%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#fbbf24" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>

            {/* Brand Name */}
            <div className="text-left">
              <h1 className="text-4xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-amber-300 bg-clip-text text-transparent">
                  Group
                </span>
                <span className="text-white ml-2">Hela</span>
              </h1>
              <p className="text-emerald-400/60 text-xs font-medium tracking-widest uppercase mt-1">
                Financial Unity Platform
              </p>
            </div>
          </div>
        </div>

        <Card className="shadow-2xl bg-[#131820]/95 border border-white/10 backdrop-blur">
          <StepIndicator currentStep={currentStep} />

          {errors.general && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <p className="text-sm text-red-200">{errors.general}</p>
            </div>
          )}

          <form
            onSubmit={
              currentStep === 3
                ? handleSubmit
                : (e) => {
                    e.preventDefault();
                    handleNext();
                  }
            }
          >
            {/* Step 1: Email */}
            {currentStep === 1 && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">What&apos;s your email?</h3>
                  <p className="text-sm text-[#d8d2c5] mb-4">
                    We&apos;ll use this for your account login
                  </p>
                </div>

                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email?.[0] || errors.email}
                  placeholder="your@email.com"
                  autoFocus
                  leftIcon={
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  }
                  required
                />

                <Button type="submit" fullWidth size="lg">
                  Continue
                </Button>

                {/* Social Authentication - Below Continue Button */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#131820] text-[#d8d2c5]">Or sign up with</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <button
                    type="button"
                    onClick={() => handleSocialAuth('google')}
                    disabled={loading}
                    className="w-full sm:flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-gray-900 font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span className="min-w-0 text-center leading-snug whitespace-normal break-words">
                      Sign up with Google
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSocialAuth('facebook')}
                    disabled={loading}
                    className="w-full sm:flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-[#1877F2] bg-[#1877F2] hover:bg-[#1565C0] text-white font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span className="min-w-0 text-center leading-snug whitespace-normal break-words">
                      Sign up with Facebook
                    </span>
                  </button>
                </div>
              </div>
            )}
            {currentStep === 2 && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Create a password</h3>
                  <p className="text-sm text-[#d8d2c5] mb-4">Make it strong and secure</p>
                </div>

                <div>
                  <Input
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={errors.password?.[0] || errors.password}
                    placeholder="Create a strong password"
                    autoFocus
                    leftIcon={
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    }
                    required
                  />

                  {formData.password && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-[#d8d2c5]">Password Strength</span>
                        <span
                          className={`text-xs font-semibold ${(() => {
                            if (passwordStrength.score <= 2) return 'text-red-400';
                            if (passwordStrength.score <= 4) return 'text-yellow-400';
                            return 'text-emerald-400';
                          })()}`}
                        >
                          {passwordStrength.label}
                        </span>
                      </div>
                      <div className="flex gap-1 h-1.5">
                        {[1, 2, 3, 4, 5, 6].map((bar) => (
                          <div
                            key={bar}
                            className={`flex-1 rounded-full transition-all duration-300 ${
                              bar <= passwordStrength.score ? passwordStrength.color : 'bg-white/10'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Input
                  label="Confirm Password"
                  name="password_confirmation"
                  type="password"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  error={errors.password_confirmation?.[0] || errors.password_confirmation}
                  placeholder="Confirm your password"
                  leftIcon={
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  }
                  required
                />

                <div className="flex gap-3">
                  <Button type="button" variant="outline" fullWidth size="lg" onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="submit" fullWidth size="lg">
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Name & Phone */}
            {currentStep === 3 && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Tell us about yourself</h3>
                  <p className="text-sm text-[#d8d2c5] mb-4">Just a couple more details</p>
                </div>

                <Input
                  label="Full Name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  error={errors.name?.[0] || errors.name}
                  placeholder="Enter your full name"
                  autoFocus
                  leftIcon={
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  }
                  required
                />

                <Input
                  label="Phone Number (Optional)"
                  name="phone_number"
                  type="tel"
                  value={formData.phone_number}
                  onChange={handleChange}
                  error={errors.phone_number?.[0] || errors.phone_number}
                  placeholder="254712345678"
                  helpText="Format: 254712345678"
                  leftIcon={
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  }
                />

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-1 rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500/50"
                    required
                  />
                  <label className="ml-2 text-sm text-[#d8d2c5]">
                    I agree to the{' '}
                    <a href="#" className="text-emerald-400 hover:text-emerald-300 font-medium">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="#" className="text-emerald-400 hover:text-emerald-300 font-medium">
                      Privacy Policy
                    </a>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" fullWidth size="lg" onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="submit" fullWidth size="lg" loading={loading}>
                    Create Account
                  </Button>
                </div>
              </div>
            )}
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#131820] text-[#d8d2c5]">Already have an account?</span>
              </div>
            </div>

            <Link to="/login" className="mt-4 block">
              <Button variant="outline" fullWidth size="lg">
                Sign In
              </Button>
            </Link>
          </div>
        </Card>

        <p className="mt-8 text-center text-sm text-[#d8d2c5]">
          Need help?{' '}
          <a href="#" className="text-emerald-400 hover:text-emerald-300">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
};

export default ProgressiveRegister;
